import { Banner } from '@ordr-as/umami-react'
import { Link } from 'react-router-dom'
import { formatDateAndTime } from '../../utils/stringUtils'
import { ClientPaymentDto } from '../../../generated-api/models/ClientPaymentDto'

type ColumnHeader = Pick<
    ClientPaymentDto,
    | 'paymentReference'
    | 'grossAmount'
    | 'terminalTipAmount'
    | 'grossAmountAfterAddingTerminalTip'
    | 'latestState'
    | 'paymentMethod'
    | 'executingLocationId'
    | 'created'
    | 'paymentProvider'
>
const columns: Record<keyof ColumnHeader, string> = {
    paymentReference: 'Payment reference',
    grossAmount: 'Original amount',
    terminalTipAmount: 'Terminal tip',
    grossAmountAfterAddingTerminalTip: 'Total amount',
    latestState: 'Status',
    paymentMethod: 'Payment method',
    executingLocationId: 'Location ID',
    created: 'Created',
    paymentProvider: 'Provider',
}

const isAmount = (item: string) =>
    item === 'grossAmount' || item === 'terminalTipAmount' || item === 'grossAmountAfterAddingTerminalTip'

const shouldShowBlank = (item: string, payment: ClientPaymentDto) =>
    item === 'terminalTipAmount' && payment.paymentMethod !== 'INTEGRATED_TERMINAL'

const PaymentList = ({ payments }: { payments: ClientPaymentDto[] }) => (
    <div className="rounded-lg bg-brand-white shadow-lg py-4 mt-4">
        <div className="overflow-x-auto">
            <div className="min-w-full inline-block">
                <div className="overflow-hidden">
                    {payments.length ? (
                        <table className="text-left min-w-full">
                            <thead className="text-grey-dark">
                                <tr>
                                    {Object.entries(columns)?.map(([, label], idx) => (
                                        <th key={idx} scope="col" className="font-normal py-2 px-5 whitespace-nowrap">
                                            {label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map((payment, idx) => (
                                    <tr key={idx} className="hover:bg-purple-lightest border-t border-grey-lightest">
                                        {Object.entries(columns).map(([col], idx) => (
                                            <td key={idx} className="py-4 px-5 whitespace-nowrap text-sm">
                                                {col === 'paymentReference' ? (
                                                    <Link
                                                        to={payment.paymentReference}
                                                        className="py-4 cursor-pointer text-brand-primary"
                                                    >
                                                        {payment[col]}
                                                    </Link>
                                                ) : isAmount(col) ? (
                                                    !shouldShowBlank(col, payment) && (
                                                        <div className="text-right">
                                                            <span className="pr-1">
                                                                {
                                                                    payment[
                                                                        col as keyof ColumnHeader
                                                                    ] as keyof ClientPaymentDto
                                                                }
                                                            </span>
                                                            <span>{payment.currency}</span>
                                                        </div>
                                                    )
                                                ) : col === 'created' ? (
                                                    <span>
                                                        {formatDateAndTime(payment.created?.toString() ?? 'No date')}
                                                    </span>
                                                ) : col === 'latestState' ? (
                                                    <span>{payment.latestState}</span>
                                                ) : col === 'paymentMethod' ? (
                                                    <span>{payment.paymentMethod}</span>
                                                ) : col === 'paymentProvider' ? (
                                                    <span>{payment.paymentProvider}</span>
                                                ) : (
                                                    <div>
                                                        {payment[col as keyof ColumnHeader] as keyof ClientPaymentDto}
                                                    </div>
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <Banner kind="warn" stretch="parent" onClose={() => null}>
                            No payments found
                        </Banner>
                    )}
                </div>
            </div>
        </div>
    </div>
)

export default PaymentList
